import React, { useState, useEffect } from "react";
import Image, { StaticImageData } from "next/image";
import styles from "./Slider.module.css";
import { Icon } from "@iconify/react";
import Button from "@/components/ui/button";
import { useExpedition } from "@/context/Expeditions";
import Loading from "@/app/loading";

interface HeroItem {
  _id: string;
  name: string;
  overview: string;
  height: string;
  banner: StaticImageData | string; // Updated to allow string from fetched data
  expeditionId?: string;
  collections?: { slug: string }; // Added for slug access
  slug?: string; // Added for navigation
  maxElevation?: string; // Added to match fetched data
}

const Slider: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(4);
  const { heroExpeditions, error } = useExpedition();
  const [isLoading, setIsLoading] = useState(true);

  // Handle responsive thumbnail visibility
  useEffect(() => {
    const updateVisibleItems = () => {
      if (window.innerWidth >= 1280) {
        setVisibleItems(4);
      } else if (window.innerWidth >= 1024) {
        setVisibleItems(3);
      } else {
        setVisibleItems(0);
      }
    };

    updateVisibleItems();
    window.addEventListener("resize", updateVisibleItems);
    return () => window.removeEventListener("resize", updateVisibleItems);
  }, []);

  // Auto-slide interval
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroExpeditions.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [heroExpeditions.length]);

  // Loading state based on data availability
  useEffect(() => {
    if (heroExpeditions && heroExpeditions.length > 0) {
      setIsLoading(false); // Only set to false when data is ready
    }
  }, [heroExpeditions]);

  const handleClick = (index: number) => setActiveIndex(index);
  const handleNext = () =>
    setActiveIndex((prev) => (prev + 1) % heroExpeditions.length);
  const handlePrev = () =>
    setActiveIndex((prev) => (prev - 1 + heroExpeditions.length) % heroExpeditions.length);

  // Show loading until data is fetched
  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#1E1E1E]">
        <Loading />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#1E1E1E] text-white">
        <p>Error loading expeditions: {error.message}</p>
      </div>
    );
  }

  return (
    <div className={styles.slider}
      style={{
        backgroundColor: 'black'
      }}>
      <div className={styles.list}>
        {heroExpeditions.map((item, index) => (
          <div
            key={item._id}
            className={`${styles.item} ${index === activeIndex ? styles.active : ""
              }`}
          >
            <div className="absolute top-0 left-0 w-full h-[20vh] z-10 bg-gradient-to-b from-[#1E1E1E] to-transparent"></div>
            <Image
              src={item.banner}
              alt={`Slider ${index + 1}`}
              width={1280}
              height={720}
              objectFit="cover"
              priority
            />
            <div
              className={`${styles.content} text-zinc-50 absolute left-[5%] 4xl:left-[9.4%] bottom-[15%] z-[1] max-w-5xl`}
            >
              <div className="flex items-center gap-2">
                <p className="w-fit px-4 py-1 rounded-full font-bold text-sm bg-orange-400/10 backdrop-blur-lg border border-orange-500 capitalize">
                  {item.collections?.slug === "trekking"
                    ? "Alpine Trekking"
                    : "Expedition"}
                </p>
                <p className="w-fit md:px-4 px-2 py-2 rounded-full font-bold md:text-sm text-xs bg-zinc-300 date text-green-600">
                  Booking Available Dates 2025-2026
                </p>
              </div>
              <div className="py-4 title font-bold text-[28px] lg:whitespace-nowrap xs:text-[40px] md:text-[60px] lg:text-[75px] 4xl:text-[110px] leading-tight flex flex-col">
                <p>{item.name}</p>
                <span className="text-[28px] xs:text-[30px] md:text-[60px] lg:text-[65px] 4xl:text-[100px]">
                  {item.maxElevation}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <a href={`/package_detail/${item.slug}`}>
                  <Button>Explore now</Button>
                </a>
                <div className={`${styles.arrows} relative flex left-2 gap-2`}>
                  <button
                    onClick={handlePrev}
                    className="flex justify-center items-center hover:bg-zinc-200 hover:scale-95 duration-200 !bg-zinc-200"
                  >
                    <Icon
                      icon="iconamoon:arrow-left-2-light"
                      className="w-7 h-7 object-cover object-center text-zinc-700"
                    />
                  </button>
                  <button
                    onClick={handleNext}
                    className="flex justify-center items-center hover:bg-zinc-200 hover:scale-95 duration-200 !bg-zinc-200"
                  >
                    <Icon
                      icon="iconamoon:arrow-right-2-light"
                      className="w-7 h-7 object-cover object-center text-zinc-700"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        className={`${styles.thumbnail} md:flex justify-end items-end hidden absolute bottom-[5%] right-[5%] 4xl:right-[9.4%] z-[11] gap-[10px] w-[50%] box-border`}
      >
        {heroExpeditions.slice(0, visibleItems).map((item, index) => (
          <div
            key={item._id}
            className={`${styles.item} ${index === activeIndex
              ? `${styles.active} border-2 border-orange-500 transition-all duration-200`
              : "border-2 border-transparent"
              } cursor-pointer`}
            onClick={() => handleClick(index)}
          >
            <Image
              width={1920}
              height={1080}
              src={item.banner}
              alt={`Thumbnail ${index + 1}`}
              objectFit="cover"
              className="brightness-50"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Slider;